import {Alert,App as AntApp,Button,Descriptions,Space,Spin,Tag,Typography} from 'antd'
import {GithubOutlined,LinkOutlined,ReloadOutlined} from '@ant-design/icons'
import {useCallback,useEffect,useRef,useState} from 'react'
import {APIError,api,mutate} from '../api'
import WizardModal from './WizardModal'
import {githubDeviceFlowBody,githubDevicePollDelay,githubTimestampMilliseconds,mergeGitHubDeviceFlow,type GitHubConnectionStatus,type GitHubDeviceFlow,type GitHubDeviceFlowUpdate} from './githubDeviceFlow'

const {Title,Text}=Typography

const flowStatusLabels={pending:'等待授权',connected:'已连接',expired:'已过期',failed:'授权失败'} as const
const flowStatusColors={pending:'processing',connected:'success',expired:'warning',failed:'error'} as const

function formatExpiry(value:number|undefined):string{
  if(!value)return '—'
  return new Date(githubTimestampMilliseconds(value)).toLocaleString('zh-CN',{hour12:false})
}

export default function GitHubDeviceLogin({canManage}:{canManage:boolean}){
  const{message}=AntApp.useApp()
  const[connection,setConnection]=useState<GitHubConnectionStatus>()
  const[statusLoading,setStatusLoading]=useState(true)
  const[statusError,setStatusError]=useState('')
  const[flow,setFlow]=useState<GitHubDeviceFlow>()
  const[pollError,setPollError]=useState('')
  const[wizardOpen,setWizardOpen]=useState(false)
  const statusRequest=useRef(0)

  const loadStatus=useCallback(async()=>{
    const request=++statusRequest.current
    setStatusLoading(true)
    setStatusError('')
    try{
      const next=await api<GitHubConnectionStatus>('/settings/github')
      if(request===statusRequest.current)setConnection(next)
    }catch(error){
      if(request===statusRequest.current)setStatusError(error instanceof Error?error.message:'GitHub 状态加载失败')
    }finally{
      if(request===statusRequest.current)setStatusLoading(false)
    }
  },[])

  useEffect(()=>{void loadStatus()},[loadStatus])

  const flowID=flow?.flow_id
  const flowStatus=flow?.status
  const flowInterval=flow?.interval
  const flowExpiresAt=flow?.expires_at
  useEffect(()=>{
    if(!flowID||flowStatus!=='pending')return
    let stopped=false
    let timer:number
    const schedule=(interval:number|undefined)=>{timer=window.setTimeout(poll,githubDevicePollDelay(interval))}
    const poll=async()=>{
      try{
        const update=await api<GitHubDeviceFlowUpdate>(`/settings/github/device-flow/${encodeURIComponent(flowID)}`)
        if(stopped)return
        setPollError('')
        setFlow(current=>current?.flow_id===flowID?mergeGitHubDeviceFlow(current,update):current)
        if(update.status==='pending')schedule(update.interval)
        else if(update.status==='connected'){
          message.success(`GitHub 已连接${update.login?`：${update.login}`:''}`)
          void loadStatus()
        }
      }catch(error){
        if(stopped)return
        if(error instanceof APIError&&error.status===404){
          setFlow(current=>current?.flow_id===flowID?{...current,status:'failed',message:'服务器已重启或登录会话已失效，请重新生成设备码。'}:current)
          return
        }
        if(flowExpiresAt&&Date.now()>=githubTimestampMilliseconds(flowExpiresAt)){
          setFlow(current=>current?.flow_id===flowID?{...current,status:'expired',message:'GitHub 设备码已过期，请重新登录。'}:current)
          return
        }
        setPollError(error instanceof Error?error.message:'暂时无法刷新 GitHub 授权状态，将自动重试。')
        schedule(flowInterval)
      }
    }
    schedule(flowInterval)
    return()=>{stopped=true;window.clearTimeout(timer)}
  },[flowExpiresAt,flowID,flowInterval,flowStatus,loadStatus,message])

  const startFlow=async(stepUpToken:string)=>{
    const next=await mutate<GitHubDeviceFlow>('/settings/github/device-flow','POST',githubDeviceFlowBody,stepUpToken)
    setFlow(next)
    setPollError('')
    setWizardOpen(false)
    if(next.status==='connected'){
      message.success(`GitHub 已连接${next.login?`：${next.login}`:''}`)
      void loadStatus()
    }
  }

  const pending=flow?.status==='pending'
  const connected=!!connection?.token_configured
  const canStart=canManage&&!!connection?.client_id_configured&&!pending
  const openWizard=()=>{setFlow(undefined);setPollError('');setWizardOpen(true)}

  return <>
    <section className="surface github-integration">
      <div className="github-integration-head">
        <div><Title level={4}><GithubOutlined/> GitHub Release</Title><Text type="secondary">使用 GitHub 设备码登录，供 Discord 的发布流程读取 Release 资源。</Text></div>
        <Space wrap><Button icon={<ReloadOutlined/>} loading={statusLoading} onClick={()=>void loadStatus()}>刷新状态</Button>{canManage&&<Button type="primary" icon={<GithubOutlined/>} disabled={!canStart} onClick={openWizard}>{connected?'重新登录':'登录 GitHub'}</Button>}</Space>
      </div>
      {statusError&&<Alert type="error" showIcon message="无法加载 GitHub 状态" description={statusError}/>}
      {!statusError&&statusLoading&&!connection?<div className="github-integration-loading"><Spin/></div>:connection&&<Descriptions size="small" bordered column={{xs:1,sm:2}} items={[
        {key:'client',label:'OAuth Client ID',children:<Tag color={connection.client_id_configured?'success':'warning'}>{connection.client_id_configured?'已配置':'未配置'}</Tag>},
        {key:'credential',label:'访问凭据',children:<Tag color={connected?'success':'default'}>{connected?'已安全保存':'未连接'}</Tag>},
        {key:'repository',label:'Release 仓库',children:connection.repository?<Text code>{connection.repository}</Text>:<Tag color="warning">未配置</Tag>},
        {key:'login',label:'GitHub 账号',children:connection.login||flow?.login||'—'},
      ]}/>}
      {connection&&!connection.client_id_configured&&<Alert type="warning" showIcon message="请先保存 GITHUB_CLIENT_ID，再启动 GitHub 设备登录。"/>}
      {connection&&!connection.repository&&<Alert type="warning" showIcon message="尚未配置 GitHub Release 仓库；连接账号后仍需填写 owner/repository。"/>}
      {flow&&<div className="github-device-flow">
        <div className="github-device-flow-title"><Text strong>设备登录</Text><Tag color={flowStatusColors[flow.status]}>{flowStatusLabels[flow.status]}</Tag></div>
        {pending&&<>
          <Text type="secondary">复制设备码，然后在 GitHub 授权页完成登录。此页面会自动刷新结果。</Text>
          <div className="github-device-code"><Text copyable={{text:flow.user_code||''}}>{flow.user_code||'正在获取设备码'}</Text></div>
          <Space wrap>{flow.verification_uri&&<Button type="primary" icon={<LinkOutlined/>} href={flow.verification_uri} target="_blank" rel="noopener noreferrer">打开 GitHub 授权页</Button>}<Spin size="small"/><Text type="secondary">有效期至 {formatExpiry(flow.expires_at)}</Text></Space>
        </>}
        {flow.status==='connected'&&<Alert type="success" showIcon message={`GitHub 已连接${flow.login?`：${flow.login}`:''}`}/>}
        {flow.status==='expired'&&<Alert type="warning" showIcon message="GitHub 设备码已过期" description={flow.message||'请重新发起登录。'}/>}
        {flow.status==='failed'&&<Alert type="error" showIcon message="GitHub 授权失败" description={flow.message||'请检查 OAuth App 配置后重试。'}/>}
        {pollError&&pending&&<Alert type="warning" showIcon message="状态刷新暂时失败" description={pollError}/>}
      </div>}
      {!canManage&&<Text type="secondary">需要 settings:update 权限才能连接或更换 GitHub 账号。</Text>}
    </section>
    <WizardModal kind="configuration" open={wizardOpen} title="连接 GitHub" onCancel={()=>setWizardOpen(false)} onConfirm={startFlow} stepUp={{path:'/settings/github/device-flow',method:'POST',body:githubDeviceFlowBody}} ready={!!connection?.client_id_configured} confirmLabel="验证并生成设备码" review={[
      {label:'操作',value:connected?'重新连接 GitHub 账号':'连接 GitHub 账号'},
      {label:'Release 仓库',value:connection?.repository||'尚未配置'},
      {label:'凭据处理',value:'访问令牌仅由服务器加密保存，控制面板不会接收或显示'},
    ]}><Alert type="info" showIcon message="Discord 二次验证完成后，服务器会生成一次性 GitHub 设备码。" description="设备码会显示在设置页；请在 GitHub 授权页输入该代码，控制面板将自动等待连接结果。"/></WizardModal>
  </>
}
